import React from 'react';
import './Login.css';
import { Alert, Col, Form as ReactForm, Row } from 'react-bootstrap';
import { ErrorMessage, Form, Formik, FormikProps } from 'formik';
import * as Yup from 'yup';
import { useSubmitForgotPasswordForm } from './authActions';
import { AppState } from '../store/store';
import { SalvButton } from '@salvapp/ui-components';
import { ExternalContainer } from '../common/ExternalContainer';
import { Turnstile } from '@marsidev/react-turnstile';
import { currentEnvironment } from '../tracking/environment';
import { connect } from 'react-redux';

const validationSchema = Yup.object().shape({
  username: Yup.string().email('Invalid email').required('Required'),
});

export interface ForgotPasswordFormValues {
  username: string;
}

const initialValues: ForgotPasswordFormValues = {
  username: '',
};

export function ForgotPassword({ forgotPasswordSent }: ForgotPasswordProps) {
  const environment = currentEnvironment();
  const { submitForgotPasswordForm, turnstileRef } = useSubmitForgotPasswordForm();

  return (
    <ExternalContainer size={50}>
      <h1 className='display-4 text-center mb-5'>Reset password</h1>
      {forgotPasswordSent ? (
        <p data-testid='forgot-password-sent'>
          If you entered a correct email, we sent you a link to reset your Salv password. Check your email.
        </p>
      ) : (
        <div>
          <p>Enter your email address. You will receive an email with a link to reset your Salv password.</p>
          <Formik initialValues={initialValues} validationSchema={validationSchema} onSubmit={submitForgotPasswordForm}>
            {({
              status,
              isSubmitting,
              errors,
              touched,
              values,
              handleChange,
            }: FormikProps<ForgotPasswordFormValues>) => (
              <Form noValidate>
                <ReactForm.Group className='form-group'>
                  <ReactForm.Label>Email Address</ReactForm.Label>
                  <ReactForm.Control
                    type='text'
                    name='username'
                    value={values.username}
                    onChange={handleChange}
                    isInvalid={touched.username && !!errors.username}
                    data-testid='username'
                  />
                  <ErrorMessage
                    name='username'
                    render={(message) => (
                      <ReactForm.Control.Feedback type='invalid'>{message}</ReactForm.Control.Feedback>
                    )}
                  />
                </ReactForm.Group>
                {environment?.cloudFlareSiteKey && (
                  <Row className='justify-content-center mb-3'>
                    <Turnstile
                      siteKey={environment.cloudFlareSiteKey}
                      options={{ appearance: 'interaction-only' }}
                      ref={turnstileRef}
                      onError={() => turnstileRef.current?.reset()}
                    />
                  </Row>
                )}
                <Col md={{ offset: 3, span: 6 }} className='text-center'>
                  <SalvButton
                    type='submit'
                    variant='primary'
                    disabled={isSubmitting}
                    label={'Reset password'}
                    data-testid='reset-password'
                  />
                </Col>
                {status && <Alert variant='danger'>{status}</Alert>}
              </Form>
            )}
          </Formik>
        </div>
      )}
    </ExternalContainer>
  );
}

const mapStateToProps = (state: AppState) => {
  const { auth } = state;
  return {
    forgotPasswordSent: auth.forgotPasswordSent,
  };
};

export type ForgotPasswordProps = ReturnType<typeof mapStateToProps>;

export default connect(mapStateToProps)(ForgotPassword);
